import {isDev} from "@prosay/common"
import {getLogger} from "@prosay/logging"
import {
    InMemoryCache,
    IntrospectionFragmentMatcher,
} from "apollo-cache-inmemory"
import {ApolloClient, DefaultOptions} from "apollo-client"
import {DocumentNode, from, split} from "apollo-link"
import {setContext} from "apollo-link-context"
import {onError} from "apollo-link-error"
import {WebSocketLink} from "apollo-link-ws"
import {createUploadLink} from "apollo-upload-client"
import {getMainDefinition} from "apollo-utilities"
import {SubscriptionClient} from "subscriptions-transport-ws"
import MessageTypes from "subscriptions-transport-ws/dist/message-types"
import {introspectionQueryResultData} from "./graphql"
import {getAccountStatus, getJwtToken as getJwtTokenUtil, logout} from "./util"

const logger = getLogger(__filename)

let _jwtToken: string | undefined
// TODO: When do we need to reload?
const getJwtToken = async (reload = false) => {
    if (reload || !_jwtToken) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        _jwtToken = await getJwtTokenUtil(client)
        logger.info`Our jwt token is ${_jwtToken}`
    }
    return _jwtToken
}

const errorLink = onError(({...errorData}) => {
    const {graphQLErrors, networkError} = errorData

    if (graphQLErrors) {
        logger.error`Got the following GraphQL errors: ${graphQLErrors}`
    }

    if (networkError) {
        logger.error`Got the following GraphQL errors: ${networkError}`
    }
})

const authLink = setContext(async (_, {headers}) => {
    const jwtToken = await getJwtToken()

    if (!isDev() && jwtToken && !getAccountStatus(jwtToken)) {
        await logout(false)
        return headers
    }

    return {
        headers: {
            ...headers,
            authorization: jwtToken ? `Bearer ${jwtToken}` : "",
        },
    }
})

const ENDPOINT = "api.prosay.app"
const ENDPOINT_SECURE = true
const ENDPOINT_SECURE_FLAG = ENDPOINT_SECURE ? "s" : ""

// we don't use createHttpLink anymore because of uploads
const uploadLink = createUploadLink({
    uri: `http${ENDPOINT_SECURE_FLAG}://${ENDPOINT}`, // TODO: Find config solution for this
})

const subscriptionClient = new SubscriptionClient(
    `ws${ENDPOINT_SECURE_FLAG}://${ENDPOINT}/graphql`,
    {
        connectionParams: async () => {
            return {
                Authorization: await getJwtToken(),
            }
        },
        // TODO:  Abstract the logic for getting jwt token out into a separate middleware
        reconnect: true,
    },
)

interface SubscriptionClientType
    extends Omit<typeof subscriptionClient, "connect" | "sendMessage"> {
    connect: Function
    sendMessage: Function
}

// we need to restart the WS connection every time the JWT changes
// we can't simply send an "update" message as there'd still be ongoing subscriptions
const reconnectSubscriptionClient = () => {
    const client = (subscriptionClient as unknown) as SubscriptionClientType

    // Close socket connection which will also unregister subscriptions on the server-side.
    client.close()

    // Reconnect to the server.
    client.connect()

    // Reregister all subscriptions.
    Object.keys(client.operations).forEach(id => {
        client.sendMessage(
            id,
            MessageTypes.GQL_START,
            client.operations[id].options,
        )
    })
}

const webSocketLink = new WebSocketLink(subscriptionClient)

const isSubscription = (node: DocumentNode) => {
    const definition = getMainDefinition(node)
    return (
        definition.kind === "OperationDefinition" &&
        definition.operation === "subscription"
    )
}

const link = split(
    ({query}) => isSubscription(query),
    webSocketLink,
    uploadLink,
)

const fragmentMatcher = new IntrospectionFragmentMatcher({
    introspectionQueryResultData,
})

const cache = new InMemoryCache({
    fragmentMatcher, // see Apollo 2.6 release notes
    freezeResults: true,
})

const defaultOptions: DefaultOptions = {
    watchQuery: {
        notifyOnNetworkStatusChange: true,
    },
}

export const client = new ApolloClient({
    assumeImmutableResults: true, // see Apollo 2.6 release notes
    cache,
    defaultOptions,
    link: from([errorLink, authLink, link]),
})

export const setCachedJwtToken = (token: string) => {
    _jwtToken = token
    reconnectSubscriptionClient()
}
